/**
 * CRM Annex Templates New - Biblioteca de plantillas de anexos (new)
 * Ruta: /crm/contratos-new/plantillas-anexos
 * Lista plantillas del backend y permite previsualizar
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { annexTemplateService } from '@/services/annexTemplateService';
import {
  ArrowLeftIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { AnnexTemplate, AnnexTemplateCategory } from '@/types/contracts';

export function CRMAnnexTemplatesNew() {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<AnnexTemplateCategory[]>([]);
  const [templates, setTemplates] = useState<AnnexTemplate[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<AnnexTemplate | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [selectedCategory]);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const data = await annexTemplateService.getCategories();
      setCategories(data);
      if (data.length > 0 && !selectedCategory) {
        setSelectedCategory(data[0].id);
      }
    } catch (error) {
      console.error('Error cargando categorías:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    if (selectedCategory === null && categories.length > 0) return;
    setLoading(true);
    try {
      const data = await annexTemplateService.getTemplates(selectedCategory ?? undefined);
      setTemplates(data);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (template: AnnexTemplate) => {
    setLoadingPreview(true);
    setPreviewTemplate(null);
    try {
      const full = await annexTemplateService.getBySlug(template.slug);
      setPreviewTemplate(full);
    } catch (error) {
      console.error('Error cargando plantilla:', error);
      setPreviewTemplate(template);
    } finally {
      setLoadingPreview(false);
    }
  };

  const categoryLabel = (id: string) =>
    categories.find((c) => c.id === id)?.label ?? id;

  return (
    <div className="w-full space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <Link
            to="/crm/contratos-new"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-1" />
            Volver a Contratos
          </Link>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Plantillas de Anexos <span className="text-sm font-normal text-gray-500">(new)</span>
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Selecciona una plantilla para usarla al crear un anexo en un contrato
          </p>
        </div>
      </div>

      {/* Categorías como tabs */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id)}
            >
              {cat.label}
              <Badge variant="secondary" className="ml-2">
                {cat.count}
              </Badge>
            </Button>
          ))}
        </div>
      )}

      {/* Lista de plantillas */}
      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="p-12">
            <EmptyState
              icon={<DocumentTextIcon className="w-12 h-12" />}
              title="No hay plantillas"
              description={
                selectedCategory
                  ? `No hay plantillas en la categoría "${categoryLabel(selectedCategory)}".`
                  : 'El backend no ha devuelto plantillas. Verifica que el endpoint /admin/hiring/annex-templates esté disponible.'
              }
            />
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card
              key={t.slug}
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handlePreview(t)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                    <p className="text-xs text-gray-500 mt-1">{t.category_label}</p>
                    {t.placeholders?.length > 0 && (
                      <p className="text-xs text-gray-400 mt-2">
                        {t.placeholders.length} placeholders
                      </p>
                    )}
                  </div>
                  <DocumentDuplicateIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePreview(t);
                  }}
                >
                  Ver plantilla
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de previsualización */}
      <Dialog open={!!previewTemplate || loadingPreview} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {loadingPreview ? 'Cargando...' : previewTemplate?.name ?? 'Plantilla'}
            </DialogTitle>
          </DialogHeader>
          {loadingPreview && (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          )}
          {previewTemplate && !loadingPreview && (
            <div className="flex-1 overflow-y-auto">
              <p className="text-sm text-gray-500 mb-2">
                Categoría: {previewTemplate.category_label} • Slug: {previewTemplate.slug}
              </p>
              <pre className="bg-gray-50 p-4 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                {previewTemplate.content ?? '(Sin contenido)'}
              </pre>
              <p className="text-xs text-gray-400 mt-2">
                Para usar esta plantilla, ve al detalle de un contrato → Anexos → Crear anexo y selecciona la plantilla.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
